import type { AvailableLanguages } from '@context'
import { Alert, Linking, Platform } from 'react-native'
import { localizedReferenceName } from './localizedReferenceName'
import {
  launchImageLibrary,
  launchCamera,
  ImagePickerResponse,
  PhotoQuality,
} from 'react-native-image-picker'

export function isSameArrays<T>(a: readonly T[], b: readonly T[]) {
  if (a.length !== b.length) return false

  const set = new Set(a)
  return b.every((id) => set.has(id))
}

export function getFormatedList(list: string[]) {
  if (list.length === 2) {
    return list.join(' et ')
  } else if (list.length > 2) {
    const nbOther = list.length - 2
    return list.slice(0, 2).join(', ') + ' et ' + nbOther + ' autre' + (nbOther > 1 ? 's' : '')
  }
  return list[0]
}

interface SelectImageLabels {
  title: string
  message: string
  cancel: string
  gallery: string
  camera: string
}

export const selectImage = (
  onImage: (uri: string, type?: string) => void,
  labels: SelectImageLabels,
) => {
  // react-native-image-picker is mocked on web (it can't open a native
  // gallery/camera), so fall back to a hidden <input type="file"> and hand
  // back a data URI that FormData can turn into a Blob.
  if (Platform.OS === 'web') {
    pickImageFromWeb(onImage)
    return
  }

  Alert.alert(labels.title, labels.message, [
    {
      text: labels.cancel,
      style: 'cancel',
    },
    {
      text: labels.gallery,
      onPress: () => openImageLibrary(onImage),
    },
    {
      text: labels.camera,
      onPress: () => openCamera(onImage),
    },
  ])
}

const pickImageFromWeb = (onImage: (uri: string, type?: string) => void) => {
  if (typeof document === 'undefined') return

  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = (event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        onImage(reader.result, file.type)
      }
    }
    reader.readAsDataURL(file)
  }
  input.click()
}

export const openImageLibrary = (onImage: (uri: string, type?: string) => void) => {
  const options = {
    mediaType: 'photo' as const,
    includeBase64: false,
    maxHeight: 2000,
    maxWidth: 2000,
    quality: 0.8 as PhotoQuality,
  }

  launchImageLibrary(options, (response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) {
      return
    }

    if (response.assets && response.assets[0]) {
      const imageUri = response.assets[0].uri
      if (imageUri) {
        onImage(imageUri, response.assets[0].type)
      }
    }
  })
}

export const openCamera = (onImage: (uri: string, type?: string) => void) => {
  const options = {
    mediaType: 'photo' as const,
    includeBase64: false,
    maxHeight: 2000,
    maxWidth: 2000,
    quality: 0.8 as PhotoQuality,
  }

  launchCamera(options, (response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) {
      return
    }

    if (response.assets && response.assets[0]) {
      const imageUri = response.assets[0].uri
      if (imageUri) {
        onImage(imageUri, response.assets[0].type)
      }
    }
  })
}

export async function getCoordinatesFromCity(city: string) {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json`

  const res = await fetch(url, {
    headers: {
      'User-Agent': 'KeepTouch/2.0 contact@yourapp.com',
    },
  })

  const data = await res.json()

  if (data.length > 0) {
    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon),
    }
  }

  return null
}

export const openUrl = async (url: string | null | undefined = '') => {
  const value = (url ?? '').trim()
  if (!value) return
  // Strict scheme check: only http(s). Prepending 'https://' when no
  // scheme is given guards against `javascript:` / `data:` / `file:`
  // payloads that would otherwise execute on web (Linking.openURL
  // falls through to `window.open`).
  const safeUrl = /^https?:\/\//i.test(value) ? value : `https://${value}`
  if (!/^https?:\/\//i.test(safeUrl)) return
  if (await Linking.canOpenURL(safeUrl)) {
    Linking.openURL(safeUrl)
  }
}

/** Maps API moment code to registerStep3 translation key for best-moment labels. */
const MOMENT_I18N_KEYS: Record<string, string> = {
  coffee: 'registerStep3.coffee',
  weekend: 'registerStep3.weekend',
  aperitif: 'registerStep3.aperitif',
  breakfast: 'registerStep3.breakfast',
  dinner: 'registerStep3.dinner',
  lunch: 'registerStep3.lunch',
  walk: 'registerStep3.walk',
  video_call: 'registerStep3.videoCall',
  phone_call: 'registerStep3.phoneCall',
  sport: 'registerStep3.sport',
  event: 'registerStep3.event',
}

export function getMomentLabel(
  t: (key: string) => string,
  code: string,
  nameFr: string,
  nameEn?: string,
  lang?: AvailableLanguages,
  nameEs?: string,
): string {
  const key = MOMENT_I18N_KEYS[code]
  if (key) return t(key)
  if (lang !== undefined) {
    return localizedReferenceName(lang, nameFr, nameEn ?? nameFr, nameEs)
  }
  return nameFr
}

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export function getDayLabel(t: (key: string) => string, dayId: string | undefined): string {
  if (dayId == null || dayId === '') return ''
  const i = Number(dayId)
  if (Number.isNaN(i) || i < 0 || i > 6) return ''
  return t(`form.days.${DAY_KEYS[i]}`)
}
